import { AddPropertyModal } from '$src/components/profile/AddPropertyModal'
import { usePermissions } from '$src/lib/hooks/usePermissions'
import { usePropertyCreation } from '$src/lib/hooks/usePropertyCreation'
import { propertyHooks } from '$src/lib/trpcHooks'
import { LoadingOverlay, Stack } from '@mantine/core'
import { AppPage, PlaceholderContent } from '@markku/components'
import { useState } from 'react'
import { ProposalActivationCard } from '../subscription/components'
import { ActivityFeed } from './components/ActivityFeed'
import { BusinessPortalCard } from './components/BusinessPortalCard'
import { EmergencyWaterDamageCard } from './components/EmergencyWaterDamageCard'
import { FrontpageWelcome } from './components/FrontpageWelcome'
import { Header } from './components/Header'
import { MaintenanceCard } from './components/MaintenanceCard'
import { PropertyHeroCard } from './components/PropertyHeroCard'
import { QuickActionsList } from './components/QuickActionsList'
import { UpcomingVisitCard } from './components/UpcomingVisitCard'

export function Frontpage() {
  const { data: propertiesResponse } = propertyHooks.useList()
  const { isOwnerOf } = usePermissions()
  const { handlePropertyCreated, WelcomeModal } = usePropertyCreation()

  const [addPropertyModalOpened, setAddPropertyModalOpened] = useState(false)

  const hasNoProperties = propertiesResponse?.data.length === 0
  const isLoading = propertiesResponse?.data === undefined

  const handlePropertyCreatedAndCloseModal = (
    propertyId: number,
    propertyAddress: string,
  ) => {
    setAddPropertyModalOpened(false)
    handlePropertyCreated(propertyId, propertyAddress)
  }

  const firstProperty = propertiesResponse?.data?.[0]

  // Check if user has any active subscription across all properties
  const isPremium =
    propertiesResponse?.data.some((property) =>
      property.subscriptions?.some((sub) => sub.status === 'active'),
    ) ?? false

  // Check if user has any proposal subscription
  const proposalProperty = propertiesResponse?.data.find((property) =>
    property.subscriptions?.some((sub) => sub.status === 'proposal'),
  )
  const proposalSubscription = proposalProperty?.subscriptions?.find(
    (sub) => sub.status === 'proposal',
  )

  if (isLoading) {
    return (
      <AppPage
        autoHideHeader={false}
        title={<Header unreadNotifications={3} />}
      >
        <LoadingOverlay visible />
      </AppPage>
    )
  }

  return (
    <>
      <AppPage
        title={<Header unreadNotifications={3} />}
        autoHideHeader={false}
      >
        <Stack gap="lg">
          {hasNoProperties ? (
            <FrontpageWelcome
              onAddPropertyClick={() => setAddPropertyModalOpened(true)}
            />
          ) : (
            <>
              {proposalSubscription &&
                proposalProperty &&
                isOwnerOf(proposalProperty.customerId) && (
                  <ProposalActivationCard
                    subscription={proposalSubscription}
                    property={proposalProperty}
                  />
                )}

              {/* Demo: Property hero with live camera for Spain presentation */}
              <PlaceholderContent>
                <PropertyHeroCard />
              </PlaceholderContent>

              <UpcomingVisitCard />

              <MaintenanceCard isPremium={isPremium} property={firstProperty} />

              <BusinessPortalCard />

              <EmergencyWaterDamageCard isPremium={isPremium} />

              <PlaceholderContent>
                <ActivityFeed />
              </PlaceholderContent>

              <QuickActionsList />
            </>
          )}
        </Stack>
      </AppPage>

      <AddPropertyModal
        opened={addPropertyModalOpened}
        onClose={() => setAddPropertyModalOpened(false)}
        onPropertyCreated={handlePropertyCreatedAndCloseModal}
      />

      {WelcomeModal}
    </>
  )
}
